import type { Config } from 'jodit/esm/config';
import type { DeepPartial, HTMLTagNames, IJodit } from 'jodit/esm/types';

import { Dom } from 'jodit/esm/core/dom/dom.js';
import { css, dataBind } from 'jodit/esm/core/helpers/index.js';
import { ColorPickerWidget } from 'jodit/esm/modules/widget/index.js';

const textColorIcon = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#1f1f1f" d="M11.17 4.5h1.66l4.92 15h-1.74l-1.33-4.14H9.33L8 19.5H6.25l4.92-15Zm-1.38 9.49h4.43L12.02 7l-2.23 6.99Z" />
    <path d="M5 20h14v1.5H5z" />
  </svg>
`;

const highlightColorIcon = `
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#1f1f1f"
      d="M14.85 3.15a1.5 1.5 0 0 1 2.12 0l3.88 3.88a1.5 1.5 0 0 1 0 2.12l-7.4 7.4-4.77 1.03a.75.75 0 0 1-.89-.89l1.03-4.77 6.03-6.03 1.06 1.06-5.65 5.65-.45 2.08 2.08-.45 7.02-7.02-2.82-2.82-1.06 1.06-1.06-1.06 1.88-1.88Z"
    />
    <path d="M4 19.25A1.25 1.25 0 0 1 5.25 18h13.5A1.25 1.25 0 0 1 20 19.25v1.5A1.25 1.25 0 0 1 18.75 22H5.25A1.25 1.25 0 0 1 4 20.75v-1.5Z" />
  </svg>
`;

type ColorButton = {
  state: {
    disabled?: boolean;
    icon: {
      fill: string;
    };
  };
};

type ColorMode = 'background-color' | 'color';

const inlineSkipTags: Set<HTMLTagNames> = new Set(['br', 'hr']);

const findCurrentElement = (editor: IJodit, current: Node | null): HTMLElement | null => {
  if (
    current &&
    current !== editor.editor &&
    Dom.isNode(current) &&
    Dom.isElement(current) &&
    editor.s.isCollapsed() &&
    !Dom.isTag(current, inlineSkipTags)
  ) {
    return current as HTMLElement;
  }

  return null;
};

const findInitialColor = (editor: IJodit, current: Node | null, mode: ColorMode): string => {
  let initialColor = '';

  if (current && current !== editor.editor && Dom.isNode(current)) {
    Dom.up(
      current,
      node => {
        if (Dom.isHTMLElement(node)) {
          const value = css(node, mode, true);

          if (value) {
            initialColor = value.toString();
            return true;
          }
        }

        return false;
      },
      editor.editor,
    );
  }

  return initialColor;
};

const updateButtonFill = (
  editor: IJodit,
  button: ColorButton,
  bindingKey: string,
  mode: ColorMode,
) => {
  const boundColor: unknown = dataBind(button, bindingKey);
  const applyFill = (value?: string) => {
    button.state.icon.fill = value && value !== css(editor.editor, mode).toString() ? value : '';
  };

  if (typeof boundColor === 'string' && boundColor.length > 0) {
    applyFill(boundColor);
    return;
  }

  const current = editor.s.current();

  if (current && !button.state.disabled) {
    const currentBox =
      Dom.closest(current, node => Dom.isElement(node), editor.editor) ?? editor.editor;

    applyFill(css(currentBox, mode).toString());
    return;
  }

  button.state.icon.fill = '';
};

const createColorPopup = (
  editor: IJodit,
  current: Node | null,
  close: () => void,
  button: ColorButton,
  mode: ColorMode,
  bindingKey: string,
) => {
  const currentElement = findCurrentElement(editor, current);
  const initialColor = findInitialColor(editor, current, mode);

  return ColorPickerWidget(
    editor,
    value => {
      if (currentElement) {
        if (mode === 'color') {
          currentElement.style.color = value;
        } else {
          currentElement.style.backgroundColor = value;
        }
      } else {
        editor.execCommand(mode === 'color' ? 'forecolor' : 'background', false, value);
      }

      dataBind(button, bindingKey, value);
      close();
    },
    initialColor,
  );
};

export type InlineImageUploader = (file: File) => Promise<string>;

const buildUploaderConfig = (imageUploader?: InlineImageUploader) => {
  if (!imageUploader) {
    return { insertImageAsBase64URI: true };
  }
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    customUploadFunction: async (requestData: any, showProgress: (progress: number) => void) => {
      const files = requestData instanceof FormData ? collectFiles(requestData) : [];
      showProgress(5);
      const urls: string[] = [];
      for (const [index, file] of files.entries()) {
        urls.push(await imageUploader(file));
        showProgress(Math.round(((index + 1) / Math.max(files.length, 1)) * 100));
      }
      return {
        data: { baseurl: '', files: urls, isImages: urls.map(() => true) },
        success: true,
        time: '',
      };
    },
    insertImageAsBase64URI: false,
    url: 'inline',
  };
};

const collectFiles = (formData: FormData): File[] =>
  [...formData.values()].filter((value): value is File => value instanceof File);

export const getRichTextFieldConfig = (params: {
  disabled?: boolean;
  height?: number;
  imageUploader?: InlineImageUploader;
  placeholder?: string;
  style?: { fontFamily?: string; fontSize?: string };
}): DeepPartial<Config> => {
  const { disabled, height, imageUploader, placeholder, style } = params;

  return {
    allowResizeX: false,
    allowResizeY: false,
    askBeforePasteFromWord: false,
    askBeforePasteHTML: false,
    buttons: [
      'font',
      'fontsize',
      {
        group: 'color',
        icon: textColorIcon,
        isVisible: (editor: IJodit) => !editor.o.disablePlugins.includes('color'),
        name: 'textColor',
        popup: (editor: IJodit, current: Node | null, close: () => void, button: ColorButton) =>
          createColorPopup(editor, current, close, button, 'color', 'text-color'),
        tooltip: 'Kolor tekstu',
        update: (editor: IJodit, button: ColorButton) =>
          updateButtonFill(editor, button, 'text-color', 'color'),
      },
      {
        group: 'color',
        icon: highlightColorIcon,
        isVisible: (editor: IJodit) => !editor.o.disablePlugins.includes('color'),
        name: 'highlightColor',
        popup: (editor: IJodit, current: Node | null, close: () => void, button: ColorButton) =>
          createColorPopup(editor, current, close, button, 'background-color', 'highlight-color'),
        tooltip: 'Kolor podświetlenia',
        update: (editor: IJodit, button: ColorButton) =>
          updateButtonFill(editor, button, 'highlight-color', 'background-color'),
      },
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'align',
      'outdent',
      'indent',
      '|',
      'ul',
      'ol',
      '|',
      'cut',
      'copy',
      'paste',
      '|',
      'image',
      'table',
      'link',
      '|',
      'undo',
      'redo',
      '|',
      'fullsize',
    ],
    controls: {
      font: {
        list: {
          'Gill Sans MT, Gill Sans, Calibri, sans-serif': 'Gill Sans',
        },
      },
    },
    defaultActionOnPaste: 'insert_clear_html',
    disabled,
    disablePlugins: ['add-new-line'],
    height: height ?? 400,
    image: {
      editSrc: true,
      openOnDblClick: true,
    },
    placeholder: placeholder ?? '',
    readonly: disabled,
    removeButtons: ['speechRecognize', 'selectall', 'print', 'dots', 'link'],
    // @ts-expect-error: niby nie ma, a jednak jest
    showStatusbar: false,
    showWordsCounter: false,
    style: {
      fontFamily: style?.fontFamily ?? 'Gill Sans MT, Gill Sans, Calibri, sans-serif',
      fontSize: style?.fontSize ?? '14px',
    },
    toolbarAdaptive: false,
    toolbarButtonSize: 'small',
    uploader: buildUploaderConfig(imageUploader),
    useSearch: true,
  };
};
